<script lang="ts">
	import { browser } from '$app/env';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import type { CollectionItem } from '$lib/api/velatlas/response.types';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import { createLazyLoadAttachment } from '$lib/attachments/lazyLoad';
	import SimplePagination from '$lib/components/pagination/SimplePagination.svelte';
	import CollectionCard from '$lib/components/ui/collection/CollectionCard.svelte';
	import CollectionItemCard from '$lib/components/ui/collection/CollectionItemCard.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import GenericMap from '$lib/components/ui/map/GenericMap.svelte';
	import type { ViewState } from '$lib/controllers/lastView/viewState';
	import type { LabelSlug } from '$lib/features/_other/labelSlug';
	import type { CollectionMetadata } from '$lib/features/collection/model/collectionMetadata';
	import { ANALYTICS_EVENT_NAMES, FULL_SITE_URL } from '$lib/statics';
	import { constructSafePage } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import { getCollectionForCountry } from '$remote/getCollectionForCountry.remote';
	import { getCollectionMapPoints } from '$remote/getCollectionMapPoints.remote';
	import type { Snapshot } from '@sveltejs/kit';
	import { watch } from 'runed';
	import { tick } from 'svelte';
	import type { Output } from './+page.server';

	const { data }: { data: { output: Output } } = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();

	let resultsDom = $state<HTMLElement | null>(null);
	let loadMap = $state<boolean>(false);
	let mapViewState = $state<ViewState | undefined>(undefined);
	let map = $state<GenericMap | undefined>(undefined);

	const output = $derived<Output>(data.output);
	const selectedCountry = $derived<string | undefined>(browser ? (page.url.searchParams.get('country') ?? undefined) : undefined);
	const countriesWithCollectionPage = $derived<LabelSlug[]>(output.countriesWithCollectionPage);
	const metadata = $derived<CollectionMetadata>(output.metadata);
	const currentPage = $derived<number>(constructSafePage({ page: output.page, totalPages: output.totalPages }));
	const filteredItems = $derived<CollectionItem[]>(
		selectedCountry
			? await getCollectionForCountry({ countrySlug: selectedCountry, collectionSlug: metadata.slug })
			: output.pageItems
	);
	const availableCountries = $derived<LabelSlug[]>(output.uniqueCountries);
	const meta = $derived.by(() => {
		const countryCount: number = availableCountries.length;
		const exampleNames: string[] = filteredItems
			.slice(0, 15)
			.sort((a, b) => a.englishName.length - b.englishName.length)
			.slice(0, 2)
			.map((item) => item.englishName);
		return {
			title: `${metadata.pageTitle}: ${output.totalItems} to Explore in ${countryCount} ${countryCount === 1 ? `Country` : `Countries`}`,
			description: `${metadata.description} See ${exampleNames.join(', ')}, and more.`,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toAllCollection({ slug: metadata.slug, page: currentPage }).href}`,
			imageUrl: UrlUtil.getFullImageUrl(filteredItems[0].image),
			prevHref:
				currentPage > 1
					? `${FULL_SITE_URL}/${UrlUtil.toAllCollection({ slug: metadata.slug, page: currentPage - 1 }).href}`
					: undefined,
			nextHref:
				currentPage < output.totalPages
					? `${FULL_SITE_URL}/${UrlUtil.toAllCollection({ slug: metadata.slug, page: currentPage + 1 }).href}`
					: undefined
		};
	});
	const jsonLdCollectionPage = $derived({
		'@context': 'https://schema.org' as const,
		'@type': 'CollectionPage' as const,
		name: meta.title,
		description: meta.description,
		url: meta.canonicalUrl,
		dateModified: new Date(output.lastUpdatedAtMs).toISOString()
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdCollectionPage]);

	function setCountry(countrySlug: string | undefined): void {
		const nav = UrlUtil.toAllCollection({ slug: metadata.slug, page: 1 });
		const query = countrySlug ? `?country=${countrySlug}` : '';
		goto(`/${nav.href}${query}`, { noScroll: true, keepFocus: true });
	}

	function scrollToResults(): void {
		tick().then(() => resultsDom?.scrollIntoView());
	}

	const observeMapLoading = createLazyLoadAttachment(() => (loadMap = true), '300px');

	watch([() => mapViewState, () => map], ([mapViewState, map], [prevMapViewState, prevMap]) => {
		const firstMapLoad = prevMap === undefined && map !== undefined;
		const shouldRepositionMap = firstMapLoad && mapViewState !== undefined;
		if (shouldRepositionMap) {
			map.jumpTo(mapViewState);
		}
	});

	$effect(() => {
		if (selectedCountry !== undefined) {
			analyticsApi.sendAnalytics({
				event: ANALYTICS_EVENT_NAMES.filtered,
				payload: { country: selectedCountry }
			});
		}
	});

	export const snapshot: Snapshot<{
		mapViewState: ViewState | undefined;
	}> = {
		capture: () => {
			console.debug('Capturing state');
			return {
				mapViewState
			};
		},
		restore: (value) => {
			console.debug('Restoring state', value);
			mapViewState = value.mapViewState;
		}
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	{#if meta.prevHref}
		<link rel="prev" href={meta.prevHref} />
	{/if}
	{#if meta.nextHref}
		<link rel="next" href={meta.nextHref} />
	{/if}

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={metadata.pageTitle}
	description={metadata.description}
	breadcrumbs={data.output.breadcrumbs}
	extraInfos={[
		currentPage > 1 ? `page ${currentPage}` : null,
		`${output.totalItems} place${output.totalItems === 1 ? '' : 's'} found`,
		`updated in ${new Date(output.lastUpdatedAtMs).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`
	].filter((info) => info !== null)}
	decorations={{ coordinates: filteredItems.flatMap((item) => item.coordinates).slice(0, 10) }}
/>

<div class="container mx-auto flex flex-col gap-12 px-4 py-14 md:py-16 lg:px-0">
	<section>
		<header class="flex justify-between px-2">
			<h2 class="text-2xl">{metadata.collectionTitle} On A Map</h2>
			<span class="hidden text-sm font-medium text-base-content/70 sm:block"> Click a pin to explore </span>
		</header>
		<div class="divider"></div>
		{@render MapContainer()}
	</section>

	<section bind:this={resultsDom} id="results" class="scroll-mt-20">
		<header class="flex flex-col gap-4 px-2 sm:flex-row sm:items-center sm:justify-between">
			<div class="flex items-baseline gap-2">
				<h2 class="text-2xl">Results</h2>
				<span class="font-mono text-xs text-base-content/70">
					{#if output.totalPages === 1 || selectedCountry}
						({filteredItems.length})
					{:else}
						({filteredItems.length} / {output.totalItems})
					{/if}
				</span>
			</div>
			{@render CountryFilter()}
		</header>

		<div class="divider"></div>

		{#if filteredItems.length > 0}
			<ul class="grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
				{#each filteredItems as item, i (item.id)}
					<li>
						<CollectionItemCard {item} loading={i < 3 ? 'eager' : 'lazy'} fetchPriority={i < 3 ? 'high' : 'auto'} />
					</li>
				{/each}
			</ul>
		{:else}
			<div class="rounded-box border border-dashed border-base-300 p-8 text-center text-base-content/70">
				No places found for this country filter.
			</div>
		{/if}
	</section>

	{#if output.totalPages > 1 && selectedCountry === undefined}
		<SimplePagination
			{currentPage}
			totalPages={output.totalPages}
			constructNewNav={(page) => UrlUtil.toAllCollection({ slug: metadata.slug, page })}
			doOnPreviousPageChange={scrollToResults}
			doOnSpecificPageChange={scrollToResults}
			doOnNextPageChange={scrollToResults}
		/>
	{/if}

	<section aria-labelledby="browse-by-country-heading" class="mt-4">
		<h3 id="browse-by-country-heading" class="mb-2 text-xl font-medium">{metadata.collectionTitle} by Country</h3>
		<ul class="flex list-none flex-wrap gap-x-1 gap-y-2 p-0">
			{#each countriesWithCollectionPage as country, i (country.slug)}
				{const nav = $derived<NavLink>(UrlUtil.toCountryCollection({ countrySlug: country.slug, collectionSlug: metadata.slug }))}
				<li class="flex items-center">
					<a
						class="link text-sm link-primary link-hover"
						href={`/${nav.href}`}
						data-sveltekit-preload-data={nav.preloadData}
						data-sveltekit-preload-code={nav.preloadCode}
					>
						{country.label}
					</a>
					{#if i < countriesWithCollectionPage.length - 1}
						<span class="mx-2 text-base-content/30" aria-hidden="true">·</span>
					{/if}
				</li>
			{/each}
		</ul>
	</section>

	<div class="divider my-2"></div>

	<section class="flex flex-col gap-4">
		<div class="max-w-2xl">
			<h2 class="mb-2 text-2xl">Related Collections</h2>
			<i>Explore other collections that share similar themes or topics.</i>
		</div>

		<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
			{#each output.relatedCollections as collection, i (collection.metadata.slug)}
				<li>
					<CollectionCard {collection} index={i} nav={UrlUtil.toAllCollection({ slug: collection.metadata.slug })} />
				</li>
			{/each}
		</ul>
	</section>
</div>

{#snippet CountryFilter()}
	{const manyCountriesAvailable = $derived<boolean>(availableCountries.length > 8)}

	<div aria-label="Filter results by country" class="max-w-full">
		{#if manyCountriesAvailable}
			{@render DropdownFilter()}
		{:else}
			{@render PillFilter()}
		{/if}
	</div>

	{#snippet DropdownFilter()}
		<label for="country-filter-select" class="sr-only">Filter by country</label>
		<select
			id="country-filter-select"
			class="select-bordered select w-full rounded-lg font-mono text-xs select-sm lg:w-xs"
			value={selectedCountry ?? undefined}
			defaultValue={undefined}
			onchange={(e) => setCountry(e.currentTarget.value || undefined)}
		>
			<option value={undefined}>All Countries</option>
			{#each availableCountries as country (country.slug)}
				<option value={country.slug}>
					{country.label}
				</option>
			{/each}
		</select>
	{/snippet}

	{#snippet PillFilter()}
		<ul class="m-0 flex scrollbar-none list-none items-center gap-1.5 overflow-x-auto p-0 pb-1">
			{const isAllSelected = $derived(selectedCountry === undefined)}
			<li>
				{@render PillBtn('All', isAllSelected, undefined)}
			</li>
			{#each availableCountries as country (country.slug)}
				{const isSelected = $derived(selectedCountry === country.slug)}
				<li>
					{@render PillBtn(country.label, isSelected, country.slug)}
				</li>
			{/each}
		</ul>

		{#snippet PillBtn(label: string, isSelected: boolean, value: string | undefined)}
			<button
				type="button"
				onclick={() => setCountry(value)}
				class={{
					'btn rounded-full btn-outline font-mono font-normal whitespace-nowrap btn-primary btn-xs sm:btn-sm': true,
					'btn-active': isSelected
				}}
				aria-pressed={isSelected}
			>
				{label}
			</button>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet MapContainer()}
	<figure aria-label="Map of {metadata.collectionTitle.toLowerCase()} from all over the world">
		<div {@attach observeMapLoading} class="h-[60vh] w-full overflow-hidden rounded-box shadow-sm">
			{#if browser && loadMap}
				{#await Promise.all( [import('$lib/components/ui/map/GenericMap.svelte'), getCollectionMapPoints( { collectionSlug: metadata.slug } )] ) then [{ default: GenericMap }, mapPoints]}
					<GenericMap bind:this={map} points={mapPoints} lockToArea={false} bind:viewState={mapViewState} scrollZoom={true} />
				{/await}
			{:else}
				<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
					<span class="loading mr-2 loading-spinner text-primary"></span>
					<span>Loading map of {output.totalItems} places...</span>
				</div>
			{/if}
		</div>
		<figcaption class="sr-only">
			Interactive map showing locations of {metadata.collectionTitle.toLowerCase()}, including
			{new Intl.ListFormat('en-GB', { style: 'short', type: 'conjunction' }).format(
				filteredItems.slice(0, 5).map((c) => c.englishName)
			)} and more.
		</figcaption>
	</figure>
{/snippet}
